import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Typography, Box, Button, LinearProgress, Chip, Modal, TextField, Radio, RadioGroup, FormControlLabel, FormLabel } from '@mui/material';
import { DownloadRounded as DownloadIcon } from '@mui/icons-material';
import WebcamCard, { WebcamCardHandle, VideoQualityWarning } from '../components/WebcamCard';
import SignalsPanel from '../components/SignalsPanel';
import HealthCard from '../components/HealthCard';
import { useStatsSettings } from '../context/Context';
import { useNavigate } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { apiFetch, trackEvent } from '../config/api';
import { logError } from '../utils/logError';
import { getModelCapabilities } from '../utils/modelCapabilities';

const ema = (prev: number, next: number, alpha = 0.5) =>
  prev === 0 ? next : Math.round(alpha * next + (1 - alpha) * prev);

const MonitorPage: React.FC = () => {
  const { healthData, updateHealthData, healthHistory, selectedStats, selectedModel } = useStatsSettings();
  const navigate = useNavigate();
  const smoothedRef = useRef({ hr: 0, sbp: 0, dbp: 0, map: 0, hrv: 0, rr: 0 });

  const [isMonitoring, setIsMonitoring] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [ppgSignal, setPpgSignal] = useState<number[]>([]);
  const [ecgSignal, setEcgSignal] = useState<number[]>([]);
  const [icgSignal, setIcgSignal] = useState<number[]>([]);
  const [bpSignal, setBpSignal] = useState<number[]>([]);
  const [bufferFrames, setBufferFrames] = useState<number | undefined>(undefined);
  const [isFirstResponseReceived, setIsFirstResponseReceived] = useState(false);
  const [mediapipeFailed, setMediapipeFailed] = useState(false);
  const [inferenceError, setInferenceError] = useState('');
  const [inferenceProgress, setInferenceProgress] = useState(0);
  const [framesCollected, setFramesCollected] = useState(0);
  const [framesRequired, setFramesRequired] = useState(0);
  const [qualityWarnings, setQualityWarnings] = useState<VideoQualityWarning[]>([]);
  const [hasBP, setHasBP] = useState(false);
  const [scanSeconds, setScanSeconds] = useState(0);
  const [showSurvey, setShowSurvey] = useState(false);
  const [surveyAnswers, setSurveyAnswers] = useState({ q1: '', q2: '', q3: '', q4: '', q5: '' });
  const [surveySubmitting, setSurveySubmitting] = useState(false);
  const surveyScanCountRef = useRef(0);

  useEffect(() => {
    if (!isMonitoring) { setScanSeconds(0); return; }
    const t = setInterval(() => setScanSeconds(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [isMonitoring]);

  const handleInferenceProgress = useCallback(
    (progress: number, collected: number, required: number) => {
      setInferenceProgress(progress);
      setFramesCollected(collected);
      setFramesRequired(required);
    },
    [],
  );

  const sessionStartRef = useRef<Date | null>(null);
  const readingsRef = useRef<Array<{
    timestamp: string; hr: number; sbp: number; dbp: number;
    hrv: number; rr: number; spo2?: number; temperature?: number;
  }>>([]);
  // Full, unfiltered per-response payloads for the completed scan — same
  // shape as /ws/rhythmnet's raw output (plus a elapsed-seconds marker),
  // so this is directly comparable to Navid's own reference pipeline
  // output rather than the display-only subset readingsRef keeps.
  const fullResultsRef = useRef<Array<Record<string, unknown> & { _t: number }>>([]);
  const [downloadReady, setDownloadReady] = useState(false);
  const [sessionVideoBlob, setSessionVideoBlob] = useState<Blob | null>(null);

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleDownloadVideo = () => {
    if (sessionVideoBlob) downloadBlob(sessionVideoBlob, `seevita-scan-${Date.now()}.webm`);
  };

  const handleDownloadResults = () => {
    const payload = {
      generated_at: new Date().toISOString(),
      model: selectedModel,
      results: fullResultsRef.current,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    downloadBlob(blob, `seevita-results-${Date.now()}.json`);
  };

  const selectedModelLower = selectedModel.toLowerCase();
  const isDeepPhysModel = selectedModelLower.includes('deepphys');
  const isPhysFormerModel = selectedModelLower.includes('physformer');
  const isBPFormerModel = selectedModelLower.includes('bpformer');
  const isRhythmNetModel = selectedModelLower.includes('rhythmnet');
  const isSeeVitaModel = selectedModelLower.includes('seevita') || isDeepPhysModel || isPhysFormerModel || isBPFormerModel || isRhythmNetModel;
  const expectedInitialFrames = isBPFormerModel ? 300 : isRhythmNetModel ? 160 : isPhysFormerModel ? 160 : isDeepPhysModel ? 180 : isSeeVitaModel ? 180 : 50;
  const modelCapabilities = useMemo(() => getModelCapabilities(selectedModel), [selectedModel]);


  const webcamCardRef = useRef<WebcamCardHandle>(null);

  const maybeTriggerSurvey = () => {
    const count = parseInt(localStorage.getItem('sv_completed_scans') ?? '0', 10) + 1;
    localStorage.setItem('sv_completed_scans', String(count));
    localStorage.setItem('sv_last_scan_date', new Date().toISOString().slice(0, 10));
    if (count % 4 === 0) {
      surveyScanCountRef.current = count;
      setSurveyAnswers({ q1: '', q2: '', q3: '', q4: '', q5: '' });
      setShowSurvey(true);
      trackEvent('survey_triggered', { scanCount: count });
    }
  };

  const submitSurvey = async (skip: boolean) => {
    setSurveySubmitting(true);
    if (!skip) {
      apiFetch('/api/surveys', {
        method: 'POST',
        body: JSON.stringify({
          answers: surveyAnswers,
          scanCount: surveyScanCountRef.current,
          skipped: false,
          completedAt: new Date().toISOString(),
        }),
      }).catch(() => {});
      trackEvent('survey_completed', { scanCount: surveyScanCountRef.current });
    } else {
      apiFetch('/api/surveys', {
        method: 'POST',
        body: JSON.stringify({
          answers: {},
          scanCount: surveyScanCountRef.current,
          skipped: true,
          completedAt: new Date().toISOString(),
        }),
      }).catch(() => {});
      trackEvent('survey_skipped', { scanCount: surveyScanCountRef.current });
    }
    setSurveySubmitting(false);
    setShowSurvey(false);
  };

  const handleStartMonitoring = () => {
    if (!selectedModel) {
      setInferenceError('Choose a prediction model from the dashboard settings before starting a session.');
      return;
    }
    if (!isMonitoring) {
      setIsLoading(true);
      setIsFirstResponseReceived(false);
      setInferenceError('');
      setInferenceProgress(0);
      setFramesCollected(0);
      setFramesRequired(expectedInitialFrames);
      sessionStartRef.current = new Date();
      readingsRef.current = [];
      fullResultsRef.current = [];
      setDownloadReady(false);
      setSessionVideoBlob(null);
      setTimeout(() => {
        setIsMonitoring(true);
        setIsLoading(false);
        webcamCardRef.current?.toggleInference();
        webcamCardRef.current?.startSessionRecording();
        trackEvent('scan_started', { model: selectedModel });
      }, 800);
    } else {
      const endedAt = new Date();
      const startedAt = sessionStartRef.current ?? endedAt;
      const readings = readingsRef.current;
      if (readings.length > 0) {
        apiFetch('/api/sessions', {
          method: 'POST',
          body: JSON.stringify({
            modelUsed: selectedModel,
            startedAt: startedAt.toISOString(),
            endedAt: endedAt.toISOString(),
            durationSeconds: Math.round((endedAt.getTime() - startedAt.getTime()) / 1000),
            readings,
          }),
        }).catch(() => {});
        trackEvent('scan_completed', { model: selectedModel, readings: readings.length });
        maybeTriggerSurvey();
      } else {
        trackEvent('scan_stopped_early', { model: selectedModel });
      }
      setIsMonitoring(false);
      setInferenceProgress(0);
      setFramesCollected(0);
      setFramesRequired(0);
      smoothedRef.current = { hr: 0, sbp: 0, dbp: 0, map: 0, hrv: 0, rr: 0 };
      setHasBP(false);
      webcamCardRef.current?.toggleInference();
      webcamCardRef.current?.stopSessionRecording().then((blob) => {
        setSessionVideoBlob(blob);
        setDownloadReady(fullResultsRef.current.length > 0 || !!blob);
      });
    }
  };

  const handleQualityWarning = (warnings: VideoQualityWarning[]) => {
    setQualityWarnings(warnings);
  };

  const handleApiResponse = (response: { SBP: number[] | null; DBP: number[] | null; PPG: number[]; HR: number; RR: number; HRV: number; ECG: number[]; ICG?: number[]; BP_wave?: number[]; MAP?: number | null; SpO2?: number | null; buffer_frames?: number; signal_quality?: { snr_db: number; warnings: string[] } }) => {
    if (mediapipeFailed) return;
    setIsFirstResponseReceived(true);
    setInferenceProgress(100);
    setInferenceError('');
    // Merge signal-level warnings from backend with video-level warnings already shown
    if (response.signal_quality?.warnings?.length) {
      setQualityWarnings((prev) => {
        const backendWarnings = response.signal_quality!.warnings as VideoQualityWarning[];
        const base = prev.filter(w => !['noisy_signal','hr_out_of_range'].includes(w));
        const merged = base.concat(backendWarnings.filter(w => !base.includes(w)));
        return merged;
      });
    }
    const s = smoothedRef.current;
    s.hr  = ema(s.hr,  Math.round(response.HR  || 0));
    const bpAvailable = response.SBP?.[0] != null && response.DBP?.[0] != null;
    if (bpAvailable) {
      s.sbp = ema(s.sbp, Math.round(response.SBP![0]));
      s.dbp = ema(s.dbp, Math.round(response.DBP![0]));
      setHasBP(true);
    }
    s.rr  = ema(s.rr,  Math.round(response.RR  || 0));
    s.hrv = ema(s.hrv, Math.round(response.HRV || 0));
    if (response.MAP != null) s.map = ema(s.map, Math.round(response.MAP));
    updateHealthData({
      ...(bpAvailable ? { systolicBP: s.sbp, diastolicBP: s.dbp } : {}),
      ...(response.MAP != null ? { map: s.map } : {}),
      ...(response.SpO2 != null ? { spo2: Math.round(response.SpO2) } : {}),
      heartRate: s.hr,
      respiratoryRate: s.rr,
      hrv: s.hrv,
    });
    readingsRef.current.push({
      timestamp: new Date().toISOString(),
      hr: s.hr, sbp: s.sbp, dbp: s.dbp, hrv: s.hrv, rr: s.rr,
    });
    if (response.PPG) setPpgSignal(response.PPG);
    if (response.ECG) setEcgSignal(response.ECG);
    if (response.ICG && response.ICG.length > 0) setIcgSignal(response.ICG);
    if (response.BP_wave && response.BP_wave.length > 0) setBpSignal(response.BP_wave);
    if (typeof response.buffer_frames === 'number') setBufferFrames(response.buffer_frames);
    const startedAt = sessionStartRef.current;
    fullResultsRef.current.push({
      ...response,
      _t: startedAt ? +((Date.now() - startedAt.getTime()) / 1000).toFixed(2) : 0,
    });
  };

  const handleApiError = (message: string) => {
    setInferenceError(message);
    setIsMonitoring(false);
    setIsLoading(false);
    setInferenceProgress(0);
    setFramesCollected(0);
    setFramesRequired(0);
    logError('inference_error', message, { model: selectedModel });
  };

  const handleMediaPipeError = (failed: boolean) => {
    setMediapipeFailed(failed);
    if (failed) {
      trackEvent('scan_failed', { reason: 'mediapipe_error', model: selectedModel });
      logError('mediapipe_error', 'MediaPipe face detection failed — scan stopped', { model: selectedModel });
      setIsMonitoring(false);
      setIsLoading(false);
      setPpgSignal([]);
      setEcgSignal([]);
      setIcgSignal([]);
      setBpSignal([]);
      setInferenceProgress(0);
      setFramesCollected(0);
      setFramesRequired(0);
    }
  };

  const hasData = isMonitoring && isFirstResponseReceived;

  const computeWellness = () => {
    if (!hasData) return { score: 0, label: '—', color: '#60a5fa' };
    const hr = healthData.heartRate;
    const hrScore = hr >= 60 && hr <= 80 ? 100 : hr >= 50 && hr <= 100 ? 80 : hr >= 40 && hr <= 120 ? 50 : 20;
    const spo2Score = healthData.spo2 >= 98 ? 100 : healthData.spo2 >= 95 ? 75 : healthData.spo2 >= 90 ? 40 : 10;
    const bpScore = hasBP
      ? (() => {
          const sbp = healthData.systolicBP < 120 ? 100 : healthData.systolicBP < 130 ? 75 : healthData.systolicBP < 140 ? 50 : 20;
          const dbp = healthData.diastolicBP < 80 ? 100 : healthData.diastolicBP < 90 ? 60 : 25;
          return (sbp + dbp) / 2;
        })()
      : 75;
    const rrScore = healthData.respiratoryRate >= 12 && healthData.respiratoryRate <= 20 ? 100 : 60;
    const hrvScore = Math.min(100, (healthData.hrv / 50) * 100);
    const score = Math.round(Math.max(0, Math.min(100, hrScore * 0.30 + spo2Score * 0.25 + bpScore * 0.25 + rrScore * 0.10 + hrvScore * 0.10)));
    const label = score >= 85 ? 'Excellent' : score >= 70 ? 'Good' : score >= 50 ? 'Fair' : 'Low';
    const color = score >= 85 ? '#4ade80' : score >= 70 ? '#60a5fa' : score >= 50 ? '#fbbf24' : '#f87171';
    return { score, label, color };
  };
  const wellness = computeWellness();

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #0a1628 0%, #0d1f3c 60%, #0a1628 100%)',
      px: { xs: 1.5, md: 3, lg: 4 },
      py: { xs: 1.5, md: 2.5 },
      display: 'flex',
      flexDirection: 'column',
      gap: { xs: 1.5, md: 2 },
      maxWidth: 1600,
      mx: 'auto',
      width: '100%',
    }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button
          startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
          onClick={() => navigate('/')}
          sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'none', fontSize: '0.82rem', fontWeight: 500, '&:hover': { color: 'white', bgcolor: 'transparent' } }}
        >
          Back
        </Button>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: 'rgba(255,255,255,0.85)', letterSpacing: '4px', textTransform: 'uppercase' }}>
          SeeVita
        </Typography>
        {/* status pill */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minWidth: 80, justifyContent: 'flex-end' }}>
          {isMonitoring && !mediapipeFailed && (
            <>
              <Box sx={{
                width: 7, height: 7, borderRadius: '50%',
                bgcolor: hasData ? '#4ade80' : '#fbbf24',
                boxShadow: hasData ? '0 0 6px #4ade80' : '0 0 6px #fbbf24',
                animation: 'pulse 1.8s ease-in-out infinite',
                '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
              }} />
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: hasData ? '#4ade80' : '#fbbf24' }}>
                {hasData ? 'Live' : 'Collecting…'}
              </Typography>
            </>
          )}
        </Box>
      </Box>

      {inferenceError && (
        <Alert severity="warning" sx={{ borderRadius: '12px', py: 0.5 }}>
          {inferenceError}
        </Alert>
      )}

      {/* ── Main grid ── */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '360px 1fr', lg: '420px 1fr' },
        gap: { xs: 1.5, md: 2.5 },
        flex: 1,
        minHeight: 0,
        alignItems: 'start',
      }}>

        {/* ── Left column: camera + controls ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

          {/* Camera card */}
          <Box sx={{ ...cardSx, p: 0, overflow: 'hidden', position: 'relative' }}>
            <Box sx={{ width: '100%', aspectRatio: { xs: '3 / 4', md: '1 / 1' }, overflow: 'hidden', borderRadius: '20px' }}>
              <WebcamCard
                ref={webcamCardRef}
                source="webcam"
                mode="api"
                hideControls
                onApiResponse={handleApiResponse}
                onApiError={handleApiError}
                onMediaPipeError={handleMediaPipeError}
                onQualityWarning={handleQualityWarning}
                onInferenceProgress={handleInferenceProgress}
              />
            </Box>
          </Box>

          {/* Quality warnings */}
          {isMonitoring && qualityWarnings.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7 }}>
              {qualityWarnings.map((w) => (
                <Chip
                  key={w}
                  label={QUALITY_WARNING_LABELS[w] ?? w}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(251,191,36,0.15)',
                    color: '#fbbf24',
                    border: '1px solid rgba(251,191,36,0.3)',
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    height: 22,
                  }}
                />
              ))}
            </Box>
          )}

          {/* Progress — only while collecting before first result */}
          {isMonitoring && !isFirstResponseReceived && !mediapipeFailed && (() => {
            const total = framesRequired || expectedInitialFrames;
            const secsLeft = Math.max(0, Math.ceil((total - framesCollected) / 30));
            return (
              <Box sx={{ ...cardSx, py: 1.5, gap: 1, alignItems: 'center' }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase' }}>
                  {isSeeVitaModel ? 'Hold still' : 'Preparing…'}
                </Typography>
                {isSeeVitaModel && (
                  <Typography sx={{
                    fontSize: '3rem', fontWeight: 900, lineHeight: 1,
                    color: secsLeft <= 2 ? '#4ade80' : '#60a5fa',
                    fontVariantNumeric: 'tabular-nums',
                    transition: 'color 0.4s ease',
                  }}>
                    {secsLeft}
                  </Typography>
                )}
                <LinearProgress
                  variant="determinate"
                  value={isSeeVitaModel ? inferenceProgress : 100}
                  sx={{
                    width: '100%', height: 4, borderRadius: 999,
                    bgcolor: 'rgba(255,255,255,0.07)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: secsLeft <= 2 ? '#4ade80' : '#60a5fa',
                      borderRadius: 999,
                      transition: 'background-color 0.4s ease',
                    },
                  }}
                />
              </Box>
            );
          })()}

          {/* Start / Stop button */}
          <Button
            variant="contained"
            onClick={handleStartMonitoring}
            disabled={isLoading || !selectedModel}
            sx={{
              py: 1.4, borderRadius: '50px', fontSize: '0.88rem', fontWeight: 700, textTransform: 'none',
              bgcolor: isMonitoring ? 'transparent' : 'white',
              color: isMonitoring ? '#f87171' : '#0a1628',
              border: isMonitoring ? '1.5px solid rgba(248,113,113,0.45)' : 'none',
              boxShadow: isMonitoring ? 'none' : '0 4px 24px rgba(255,255,255,0.12)',
              transition: 'all 0.2s ease',
              '&:hover': { bgcolor: isMonitoring ? 'rgba(239,68,68,0.08)' : '#e8f0fe', transform: 'translateY(-1px)' },
              '&:active': { transform: 'translateY(0)' },
              '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.18)', boxShadow: 'none', border: 'none' },
            }}
          >
            {isLoading ? 'Initializing…'
              : isMonitoring ? 'Stop Monitoring'
              : selectedModel ? 'Start Monitoring'
              : 'Select a model first'}
          </Button>

          {/* Download — appears after a completed scan, until the next one starts */}
          {!isMonitoring && downloadReady && (
            <Box sx={{ ...cardSx, py: 1.5, gap: 1 }}>
              <Typography sx={cardLabelSx}>Download this scan</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                  onClick={handleDownloadVideo}
                  disabled={!sessionVideoBlob}
                  sx={downloadButtonSx}
                >
                  Video (.webm)
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                  onClick={handleDownloadResults}
                  disabled={fullResultsRef.current.length === 0}
                  sx={downloadButtonSx}
                >
                  Results (.json)
                </Button>
              </Box>
              {!sessionVideoBlob && (
                <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.35)' }}>
                  Video recording wasn't available for this scan (unsupported browser or no camera track) — results are still downloadable.
                </Typography>
              )}
            </Box>
          )}

          {/* Scan duration progress */}
          {isMonitoring && isFirstResponseReceived && (() => {
            const RECOMMENDED = 30;
            const pct = Math.min(100, Math.round((scanSeconds / RECOMMENDED) * 100));
            const done = scanSeconds >= RECOMMENDED;
            return (
              <Box sx={{ ...cardSx, py: 1.5, gap: 1, alignItems: 'center' }}>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase' }}>
                    {done ? 'Great scan ✓' : 'Scan in progress'}
                  </Typography>
                  <Typography sx={{ color: done ? '#4ade80' : 'rgba(255,255,255,0.3)', fontSize: '0.68rem', fontVariantNumeric: 'tabular-nums' }}>
                    {scanSeconds}s {!done && `/ ${RECOMMENDED}s`}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  sx={{
                    width: '100%', height: 4, borderRadius: 999,
                    bgcolor: 'rgba(255,255,255,0.07)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: done ? '#4ade80' : '#60a5fa',
                      borderRadius: 999,
                      transition: 'background-color 0.4s ease',
                    },
                  }}
                />
              </Box>
            );
          })()}
        </Box>

        {/* ── Right column: health stats + signals ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 }, minHeight: 0 }}>

          {/* Health cards — on top; many people can read HR/BP/RR numbers
              who can't read a waveform at a glance */}
          {mediapipeFailed ? (
            <Box sx={cardSx}>
              <Typography sx={{ color: '#f87171', fontSize: '0.85rem' }}>
                MediaPipe failed — face detection unavailable. Please reload the page.
              </Typography>
            </Box>
          ) : (
            <Box sx={cardSx}>
              <Typography sx={cardLabelSx}>Health Stats</Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1.2, bgcolor: 'rgba(255,255,255,0.04)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.07)', opacity: hasData ? 1 : 0.3, transition: 'opacity 0.5s ease', mb: 0.5 }}>
                <Box>
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'rgba(255,255,255,0.35)', letterSpacing: '2px', textTransform: 'uppercase' }}>Wellness Score</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', mt: 0.3 }}>
                    {hasData ? wellness.label : 'Start a scan to see your score'}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '2.4rem', fontWeight: 900, color: wellness.color, lineHeight: 1 }}>
                  {hasData ? wellness.score : '—'}
                </Typography>
              </Box>

              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(auto-fit, minmax(100px, 1fr))', md: 'repeat(auto-fit, minmax(120px, 1fr))' },
                gridAutoRows: '1fr',
                gap: { xs: 1, md: 1.5 },
                opacity: hasData ? 1 : 0.3,
                transition: 'opacity 0.5s ease',
              }}>
                {selectedStats.heartRate && modelCapabilities.vitals.heartRate && <HealthCard title="Heart Rate" value={hasData ? healthData.heartRate : '—'} unit="BPM" bgColor="rgba(239,68,68,0.15)" textColor="#f87171" history={healthHistory.heartRate} />}
                {(selectedStats.systolicBP || selectedStats.diastolicBP) && modelCapabilities.vitals.systolicBP && <HealthCard title="Blood Pressure" value={hasData && hasBP ? `${healthData.systolicBP}/${healthData.diastolicBP}` : '—'} unit={hasData && hasBP && selectedStats.map && modelCapabilities.vitals.map ? `mmHg · MAP ${healthData.map}` : 'mmHg'} bgColor="rgba(59,130,246,0.15)" textColor="#60a5fa" history={healthHistory.systolicBP} />}
                {selectedStats.respiratoryRate && modelCapabilities.vitals.respiratoryRate && <HealthCard title="Resp. Rate" value={hasData ? healthData.respiratoryRate : '—'} unit="/min" bgColor="rgba(20,184,166,0.15)" textColor="#2dd4bf" history={healthHistory.respiratoryRate} />}
                {selectedStats.temperature && <HealthCard title="Temp" value={healthData.temperature} unit="°C" bgColor="rgba(245,158,11,0.15)" textColor="#fbbf24" history={healthHistory.temperature} />}
                {selectedStats.spo2 && modelCapabilities.vitals.spo2 && <HealthCard title="SpO2" value={healthData.spo2} unit="%" bgColor="rgba(34,197,94,0.15)" textColor="#4ade80" history={healthHistory.spo2} />}
                {selectedStats.hrv && modelCapabilities.vitals.hrv && <HealthCard title="HRV" value={hasData ? healthData.hrv : '—'} unit="ms" bgColor="rgba(99,102,241,0.15)" textColor="#818cf8" history={healthHistory.hrv} />}
              </Box>
            </Box>
          )}

          {/* Signal charts — below the numbers */}
          <Box sx={cardSx}>
            <Typography sx={cardLabelSx}>Signals</Typography>
            <SignalsPanel isMonitoring={isMonitoring && !mediapipeFailed} ppgSignal={ppgSignal} ecgSignal={ecgSignal} icgSignal={icgSignal} bpSignal={bpSignal} bufferFrames={bufferFrames} showEcg={modelCapabilities.waveforms.ecg} showIcg={modelCapabilities.waveforms.icg} showBp={modelCapabilities.waveforms.bp} />
          </Box>

        </Box>
      </Box>

      {/* ── Post-scan survey modal ── */}
      <Modal open={showSurvey} onClose={() => submitSurvey(true)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: 'calc(100vw - 32px)', sm: 500 },
          maxHeight: 'calc(100vh - 48px)', overflowY: 'auto',
          bgcolor: '#0d1f3c', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '20px', p: { xs: 2.5, sm: 3 },
          boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
        }}>
          <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: 'white', mb: 0.5 }}>
            Quick feedback
          </Typography>
          <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)', mb: 3 }}>
            Takes 30 seconds — helps us improve SeeVita.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box>
              <FormLabel sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', fontWeight: 700 }}>
                1. How easy was this scan to complete?
              </FormLabel>
              <RadioGroup row value={surveyAnswers.q1} onChange={e => setSurveyAnswers(a => ({ ...a, q1: e.target.value }))}
                sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                {['Very difficult', 'Difficult', 'Neutral', 'Easy', 'Very easy'].map(v => (
                  <FormControlLabel key={v} value={v} control={<Radio size="small" sx={{ color: 'rgba(255,255,255,0.3)', '&.Mui-checked': { color: '#63b3ed' } }} />}
                    label={<Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>{v}</Typography>} />
                ))}
              </RadioGroup>
            </Box>

            <Box>
              <FormLabel sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', fontWeight: 700 }}>
                2. Did the result feel easy to understand?
              </FormLabel>
              <RadioGroup row value={surveyAnswers.q2} onChange={e => setSurveyAnswers(a => ({ ...a, q2: e.target.value }))} sx={{ mt: 0.5 }}>
                {['No', 'Somewhat', 'Yes'].map(v => (
                  <FormControlLabel key={v} value={v} control={<Radio size="small" sx={{ color: 'rgba(255,255,255,0.3)', '&.Mui-checked': { color: '#63b3ed' } }} />}
                    label={<Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>{v}</Typography>} />
                ))}
              </RadioGroup>
            </Box>

            <Box>
              <FormLabel sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', fontWeight: 700 }}>
                3. How useful did this scan feel for your wellness routine?
              </FormLabel>
              <RadioGroup row value={surveyAnswers.q3} onChange={e => setSurveyAnswers(a => ({ ...a, q3: e.target.value }))}
                sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                {['Not useful', 'Not sure yet', 'Somewhat useful', 'Very useful'].map(v => (
                  <FormControlLabel key={v} value={v} control={<Radio size="small" sx={{ color: 'rgba(255,255,255,0.3)', '&.Mui-checked': { color: '#63b3ed' } }} />}
                    label={<Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>{v}</Typography>} />
                ))}
              </RadioGroup>
            </Box>

            <TextField
              multiline rows={2} placeholder="What could make this experience better? (optional)"
              value={surveyAnswers.q4}
              onChange={e => setSurveyAnswers(a => ({ ...a, q4: e.target.value }))}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white', fontSize: '0.85rem', borderRadius: '12px',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                  '&.Mui-focused fieldset': { borderColor: '#63b3ed' },
                },
                '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.3)', fontSize: '0.85rem' },
              }}
            />

            <Box>
              <FormLabel sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', fontWeight: 700 }}>
                5. Would you use this again?
              </FormLabel>
              <RadioGroup row value={surveyAnswers.q5} onChange={e => setSurveyAnswers(a => ({ ...a, q5: e.target.value }))} sx={{ mt: 0.5 }}>
                {['No', 'Maybe', 'Yes'].map(v => (
                  <FormControlLabel key={v} value={v} control={<Radio size="small" sx={{ color: 'rgba(255,255,255,0.3)', '&.Mui-checked': { color: '#63b3ed' } }} />}
                    label={<Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>{v}</Typography>} />
                ))}
              </RadioGroup>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
            <Button fullWidth variant="contained" disabled={surveySubmitting}
              onClick={() => submitSurvey(false)}
              sx={{ borderRadius: '12px', bgcolor: '#63b3ed', color: '#0a1628', fontWeight: 800, textTransform: 'none', '&:hover': { bgcolor: '#90cdf4' } }}>
              Submit
            </Button>
            <Button fullWidth variant="text" disabled={surveySubmitting}
              onClick={() => submitSurvey(true)}
              sx={{ borderRadius: '12px', color: 'rgba(255,255,255,0.4)', textTransform: 'none', '&:hover': { color: 'rgba(255,255,255,0.7)', bgcolor: 'transparent' } }}>
              Skip
            </Button>
          </Box>
        </Box>
      </Modal>

    </Box>
  );
};

const cardSx = {
  background: 'rgba(10, 28, 60, 0.6)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: '20px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.05)',
  p: { xs: 1.5, md: 2 },
  display: 'flex',
  flexDirection: 'column' as const,
};

const downloadButtonSx = {
  borderRadius: '12px',
  textTransform: 'none' as const,
  fontSize: '0.78rem',
  fontWeight: 600,
  color: 'rgba(255,255,255,0.8)',
  borderColor: 'rgba(255,255,255,0.18)',
  '&:hover': { borderColor: 'rgba(255,255,255,0.35)', bgcolor: 'rgba(255,255,255,0.05)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)', borderColor: 'rgba(255,255,255,0.08)' },
};

const cardLabelSx = {
  fontSize: '0.68rem',
  fontWeight: 700,
  color: 'rgba(255,255,255,0.35)',
  letterSpacing: '2.5px',
  textTransform: 'uppercase' as const,
  mb: { xs: 1, md: 1.5 },
};

const QUALITY_WARNING_LABELS: Record<string, string> = {
  face_too_small:      '⚠ Move closer',
  face_clipped:        '⚠ Face partially out of frame',
  excessive_movement:  '⚠ Hold still',
  low_resolution:      '⚠ Low camera resolution',
  low_brightness:      '⚠ Too dark',
  noisy_signal:        '⚠ Noisy signal',
  hr_out_of_range:     '⚠ HR reading unreliable',
};

export default MonitorPage;
